import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import EmbedModal from './EmbedModal';

const { FiPlay, FiPause, FiVolume2, FiShare2, FiZap } = FiIcons;

const CutePlayer = ({ streamUrl, logoUrl, isEmbedded = false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [metadata, setMetadata] = useState({ title: "Connecting...", artist: "On Air" });
  const [isLoading, setIsLoading] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const audioRef = useRef(null);

  const fetchMetadata = () => {
    const callbackName = 'radiojar_cb_s1_' + Math.floor(Math.random() * 1000000);
    window[callbackName] = (data) => {
      if (data && (data.title || data.artist)) {
        setMetadata({ title: data.title || "Live Stream 1", artist: data.artist || "Console Radio" });
      }
      delete window[callbackName];
      const script = document.getElementById(callbackName);
      if (script) document.body.removeChild(script);
    };

    const script = document.createElement('script');
    script.id = callbackName;
    script.src = `https://www.radiojar.com/api/stations/98mxa4t2c0nwv/now_playing/?callback=${callbackName}&_=${Date.now()}`;
    script.async = true;
    document.body.appendChild(script);
  };

  useEffect(() => {
    fetchMetadata();
    const interval = setInterval(fetchMetadata, 15000);
    return () => clearInterval(interval);
  }, []);

  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      setIsLoading(true);
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
          setIsLoading(false);
        })
        .catch(() => setIsLoading(false));
    }
  };

  return (
    <div className="w-full max-w-4xl bg-white rounded-[3rem] shadow-2xl border border-gray-100 overflow-hidden flex flex-col md:flex-row min-h-[400px] relative">
      {/* Share Button - Always Visible on Main Site */}
      {!isEmbedded && (
        <button 
          onClick={() => setIsShareOpen(true)}
          className="absolute top-6 right-6 z-10 p-3 bg-white/80 backdrop-blur-md border border-gray-100 hover:bg-[#00AEEF] hover:text-white text-gray-400 rounded-2xl transition-all shadow-sm flex items-center gap-2 group"
        >
          <SafeIcon icon={FiShare2} className="text-lg" />
          <span className="text-[10px] font-black uppercase tracking-widest overflow-hidden w-0 group-hover:w-12 transition-all">Share</span>
        </button>
      )}

      {/* Left Area: Logo */}
      <div className="w-full md:w-2/5 bg-gray-900 relative overflow-hidden shrink-0">
        <img src={logoUrl} alt="Stream 1" className={`w-full h-full object-cover transition-transform duration-[5000ms] ${isPlaying ? 'scale-110' : 'scale-100'}`} />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent opacity-60"></div>
        <div className="absolute bottom-8 left-8">
          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md px-3 py-1.5 rounded-full mb-2">
            <SafeIcon icon={FiZap} className="text-[#00AEEF] text-xs" />
            <span className="text-[8px] font-black text-white uppercase tracking-[0.2em]">High Definition</span>
          </div>
          <h3 className="text-white text-xl font-black uppercase tracking-tighter">Stream One</h3>
        </div>
      </div>

      {/* Right Area: Controls */}
      <div className="flex-1 p-8 md:p-14 flex flex-col justify-center bg-white text-center md:text-left">
        <div className="mb-10">
          <AnimatePresence mode="wait">
            <motion.div key={metadata.title} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-2">
              <h2 className="text-3xl md:text-5xl font-black text-gray-900 leading-tight tracking-tight truncate">{metadata.title}</h2>
              <p className="text-sm font-bold text-[#00AEEF] uppercase tracking-[0.2em]">{metadata.artist}</p>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-8">
          <button onClick={togglePlay} className="w-20 h-20 rounded-[2rem] bg-[#00AEEF] text-white flex items-center justify-center shadow-xl shadow-blue-100 hover:scale-105 active:scale-95 transition-all shrink-0">
            {isLoading ? <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div> : isPlaying ? <SafeIcon icon={FiPause} className="text-3xl" /> : <SafeIcon icon={FiPlay} className="text-3xl ml-1" />}
          </button>
          
          <div className="flex-1 w-full space-y-3">
             <div className="flex justify-between text-[9px] font-black text-gray-400 uppercase tracking-widest px-1">
              <span className="flex items-center gap-2"><SafeIcon icon={FiVolume2} /> Volume</span>
              <span>{Math.round(volume * 100)}%</span>
            </div>
            <input type="range" min="0" max="1" step="0.01" value={volume} onChange={(e) => {setVolume(parseFloat(e.target.value)); audioRef.current.volume = parseFloat(e.target.value);}} className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-[#00AEEF]" />
          </div>
        </div>
      </div>

      <EmbedModal isOpen={isShareOpen} onClose={() => setIsShareOpen(false)} embedPath="/embed/stream-1" width="100%" height="450px" />
      <audio ref={audioRef} src={streamUrl} crossOrigin="anonymous" />
    </div>
  );
};

export default CutePlayer;